import {
  formatDateFullText,
  isDateAfter,
} from "../../../../utils/dateProcessor";
import { addTableSpace, generateTable } from "./utils";

const getStatus = (status) => {
  return status ? "Yes" : "No";
};

const getElectionStatus = (electionDate, poaEnd) => {
  if (!electionDate) return "No";

  const isElectionDateAfterPoaEnd = isDateAfter(electionDate, poaEnd);

  return !isElectionDateAfterPoaEnd ? "Yes" : "No";
};

const getDisplayedDate = (electionDate, poaEnd) => {
  if (!electionDate) return "";

  const isElectionDateAfterPoaEnd = isDateAfter(electionDate, poaEnd);
  const displayDate = formatDateFullText(electionDate);

  return isElectionDateAfterPoaEnd
    ? displayDate + " (Future period)"
    : displayDate;
};

export const generateGroupElectionsTable = (data) => {
  const { poaData, savedData, poaEnd } = data;

  const columns = [
    { title: "Group Elections", metric: "groupElections" },
    { title: "Statutory reference", metric: "statutoryReference" },
    { title: "Duration", metric: "duration" },
    { title: "Status", metric: "status" },
    {
      title: "Period in which election was made (if provided)",
      metric: "year",
    },
  ];

  const rows = [
    {
      groupElections:
        "Election to apply the Disregard Regulations to pre-1 April 2020 derivative contracts",
      statutoryReference: "Para 31 Sch 5 Finance (No2.) Act 2017",
      duration:
        "Irrevocable but does not apply to contracts entered into after 1 April 2020",
      status: getElectionStatus(
        savedData?.disregard_regulations_election_year,
        poaEnd,
      ),
      year: getDisplayedDate(
        savedData?.disregard_regulations_election_year,
        poaEnd,
      ),
    },
    {
      groupElections: "Group ratio election",
      statutoryReference: "Para 13 Sch 7A, s398",
      duration: "Current period only",
      status: getStatus(poaData?.apply_group_ratio_election),
      year: "",
    },
    {
      groupElections: "Group EBITDA (chargeable gains) election",
      statutoryReference: "Para 15 Sch 7A, s422",
      duration: "Irrevocable",
      status: getElectionStatus(savedData?.group_ebitda_gains_year, poaEnd),
      year: getDisplayedDate(savedData?.group_ebitda_gains_year, poaEnd),
    },
    {
      groupElections: "Group ratio blended election",
      statutoryReference: "Para 14 Sch 7A, s401 - 404",
      duration: "Current period only",
      status: getStatus(poaData?.group_ratio_blended_election),
      year: "",
    },
    {
      groupElections: "Interest allowance (alternative calculation) election",
      statutoryReference: "Para 16 Sch 7A, s423 - 426",
      duration: "Irrevocable",
      status: getElectionStatus(
        savedData?.interest_allowance_alt_calc_year,
        poaEnd,
      ),
      year: getDisplayedDate(
        savedData?.interest_allowance_alt_calc_year,
        poaEnd,
      ),
    },
    {
      groupElections:
        "Interest allowance (non-consolidated investment) election",
      statutoryReference: "Para 17 Sch 7A, s427 - 429",
      duration: "Current period only",
      status: getStatus(
        poaData?.election_interest_allowance_non_consolidated_investment,
      ),
      year: "",
    },
    {
      groupElections: "Interest allowance (consolidated partnership) election",
      statutoryReference: "Para 18 Sch 7A, s430",
      duration: "Irrevocable",
      status: getElectionStatus(
        savedData?.interest_allowance_consolidated_partnership_year,
        poaEnd,
      ),
      year: getDisplayedDate(
        savedData?.interest_allowance_consolidated_partnership_year,
        poaEnd,
      ),
    },
    {
      groupElections:
        "Election not to treat consolidated IAS financial statements as deemed to be drawn up for the relevant period",
      statutoryReference: "s484",
      duration: "Current period only",
      status: getStatus(poaData?.election_consolidated_ias),
      year: "",
    },
    {
      groupElections:
        "Election to alter deemed period of account where no actual financial statements drawn up",
      statutoryReference: "s486",
      duration: "Current period only",
      status: getStatus(poaData?.election_no_statements_drawn_up),
      year: "",
    },
  ];

  const table = generateTable(rows, columns);

  return [addTableSpace(200), table];
};

const getCompanyList = (savedData, electionKey) => {
  return [
    ...new Set(
      savedData?.company_accounting_periods
        .filter((company) => company?.[electionKey] === true)
        .map((company) => company.company_name),
    ),
  ].join(", ");
};

export const generateCompanyElectionsTable = (data) => {
  const { savedData } = data;

  const electionAmortisedCostList = getCompanyList(
    savedData,
    "election_s456_s457",
  );

  const qualifyingInfrastructureCompanyList = getCompanyList(
    savedData,
    "election_s433_s434",
  );

  const columns = [
    { title: "Company Elections", metric: "companyElections" },
    { title: "Statutory reference", metric: "statutoryReference" },
    { title: "Duration", metric: "duration" },
    { title: "List of Companies", metric: "companyList" },
  ];

  const rows = [
    {
      companyElections:
        "Election for amortised cost basis for fair valued creditor relationships",
      statutoryReference: "s456, 457",
      duration: "Irrevocable",
      companyList: electionAmortisedCostList,
    },
    {
      companyElections: "Qualifying infrastructure company",
      statutoryReference: "s433, 434",
      duration: "Revocable after 5 years",
      companyList: qualifyingInfrastructureCompanyList,
    },
    {
      companyElections: "Qualifying infrastructure company - group election",
      statutoryReference: "s435",
      duration: "Revocable after 5 years",
      companyList: "Not recorded in system",
    },
    {
      companyElections:
        "Qualifying infrastructure company - joint venture companies election",
      statutoryReference: "s444 - 446",
      duration: "Revocable after 5 years",
      companyList: "Not recorded in system",
    },
  ];

  const table = generateTable(rows, columns);

  return [addTableSpace(200), table];
};
